// Roundabout Animations - 10 one-to-one mappings for roundabout rules questions
// Dutch rotonde rules: haaientanden at entry, circulating traffic has priority, signal right on exit

export interface RoundaboutAnimation {
  id: string;
  topic: string;
  sceneDescription: string;
  klingPrompt: string;
  negativePrompt: string;
  videoUrl: string | null;
  durationSeconds: number;
  viewType: 'top-down' | 'first-person' | 'third-person';
  freezeDescription: string;
  questionIds: string[];
}

export const roundaboutAnimations: RoundaboutAnimation[] = [
  {
    id: 'rr-anim-1',
    topic: 'Roundabout Rules',
    sceneDescription: 'Car approaching roundabout entry with exit signal on',
    klingPrompt: 'Top-down view of Dutch roundabout. White car entering from bottom with right indicator (exit signal) on. Approaching exit for 2nd or 3rd exit.',
    negativePrompt: 'traffic lights, stop signs, first-person, UK roundabout, USA roads, text overlay, night, rain, cartoon, CGI, anime',
    videoUrl: null,
    durationSeconds: 4,
    viewType: 'top-down',
    freezeDescription: 'Car with right indicator entering roundabout.',
    questionIds: ['q-rr-2'],
  },
  {
    id: 'rr-anim-2',
    topic: 'Roundabout Rules',
    sceneDescription: 'Traffic circulating clockwise around Dutch roundabout',
    klingPrompt: 'Top-down view of Dutch roundabout showing traffic flow. Multiple cars circulating in clockwise direction around the roundabout. Clear continuous clockwise motion, demonstrating standard Dutch roundabout direction.',
    negativePrompt: 'traffic lights, stop signs, first-person, UK roundabout, counter-clockwise, text overlay, night, rain, cartoon, CGI, anime',
    videoUrl: null,
    durationSeconds: 4,
    viewType: 'top-down',
    freezeDescription: 'Traffic moving clockwise on roundabout.',
    questionIds: ['q-rr-3'],
  },
  {
    id: 'rr-anim-3',
    topic: 'Roundabout Rules',
    sceneDescription: 'Single-lane roundabout showing traffic cannot overtake',
    klingPrompt: 'Top-down view of single-lane Dutch roundabout. Blue car circulating in the one lane. Another car ahead in the same lane. Blue car cannot change lanes — there is only one lane. Demonstrates single-lane constraint with only one available lane for all traffic.',
    negativePrompt: 'traffic lights, stop signs, first-person, UK roundabout, multi-lane, overtaking happening, text overlay, night, rain, cartoon, CGI, anime',
    videoUrl: null,
    durationSeconds: 4,
    viewType: 'top-down',
    freezeDescription: 'Single-lane roundabout with one car following another — no overtaking possible.',
    questionIds: ['q-rr-4'],
  },
  {
    id: 'rr-anim-4',
    topic: 'Roundabout Rules',
    sceneDescription: 'Car exiting roundabout with cyclist in fietspad at exit',
    klingPrompt: 'Top-down view of Dutch roundabout. Yellow car exiting with right indicator on. Red-surfaced fietspad (bicycle path) visible at the exit with a cyclist waiting or crossing. Car must yield to cyclist. Clear demonstration of cyclist priority at roundabout exit.',
    negativePrompt: 'traffic lights, stop signs, first-person, UK roundabout, text overlay, night, rain, cartoon, CGI, anime, no cyclist',
    videoUrl: null,
    durationSeconds: 4,
    viewType: 'top-down',
    freezeDescription: 'Car exiting with cyclist in fietspad — must give way to cyclist.',
    questionIds: ['q-rr-5'],
  },
  {
    id: 'rr-anim-5',
    topic: 'Roundabout Rules',
    sceneDescription: 'Car exiting roundabout, pedestrian crossing at exit',
    klingPrompt: 'Top-down view of Dutch roundabout. Green car exiting with pedestrian on zebrapad at exit. Car must yield to pedestrian.',
    negativePrompt: 'traffic lights, stop signs, first-person, UK roundabout, text overlay, night, rain, cartoon, CGI, anime',
    videoUrl: null,
    durationSeconds: 4,
    viewType: 'top-down',
    freezeDescription: 'Car exiting, pedestrian at exit crossing.',
    questionIds: ['q-rr-8'],
  },
  {
    id: 'rr-anim-6',
    topic: 'Roundabout Rules',
    sceneDescription: 'Multi-lane roundabout entry with lane selection for third exit',
    klingPrompt: 'Top-down view of multi-lane Dutch roundabout with two clearly marked lanes. Red car entering from the bottom in the left (inner) lane, intending to take the third exit (turning left). Shows correct lane discipline: left lane for 3rd exit. Multiple exits visible around the roundabout.',
    negativePrompt: 'traffic lights, stop signs, first-person, UK roundabout, text overlay, night, rain, cartoon, CGI, anime, single lane, emergency vehicle',
    videoUrl: null,
    durationSeconds: 4,
    viewType: 'top-down',
    freezeDescription: 'Car in left lane on multi-lane roundabout for third exit.',
    questionIds: ['q-rr-9'],
  },
  {
    id: 'rr-anim-7',
    topic: 'Roundabout Rules',
    sceneDescription: 'Car stopped on roundabout with congested exit road ahead',
    klingPrompt: 'Top-down view of Dutch roundabout. White car circulating, approaching its intended exit. The exit road ahead is congested/blocked with vehicles backed up. Car must NOT stop on the roundabout; must wait at the entry instead. Shows the scenario where stopping would block circulating traffic.',
    negativePrompt: 'traffic lights, stop signs, first-person, UK roundabout, text overlay, night, rain, cartoon, CGI, anime, clear exit, U-turn, emergency',
    videoUrl: null,
    durationSeconds: 4,
    viewType: 'top-down',
    freezeDescription: 'Car approaching congested exit — cannot stop on roundabout.',
    questionIds: ['q-rr-10'],
  },
  {
    id: 'rr-anim-8',
    topic: 'Roundabout Rules',
    sceneDescription: 'Car exiting roundabout with cyclist in bicycle lane',
    klingPrompt: 'Top-down view of Dutch roundabout. Red car exiting, cyclist on fietspad (bicycle path) at exit. Car must yield to cyclist.',
    negativePrompt: 'traffic lights, stop signs, first-person, UK roundabout, text overlay, night, rain, cartoon, CGI, anime',
    videoUrl: null,
    durationSeconds: 4,
    viewType: 'top-down',
    freezeDescription: 'Car exiting, cyclist in fietspad at exit.',
    questionIds: ['q-rr-14'],
  },
  {
    id: 'rr-anim-9',
    topic: 'Roundabout Rules',
    sceneDescription: 'Car taking third exit at roundabout with indicator signal',
    klingPrompt: 'Top-down view of Dutch roundabout. Blue car enters, passes first exit, passes second exit, activates right indicator for third exit. Clear third exit signal.',
    negativePrompt: 'traffic lights, stop signs, first-person, UK roundabout, text overlay, night, rain, cartoon, CGI, anime',
    videoUrl: null,
    durationSeconds: 5,
    viewType: 'top-down',
    freezeDescription: 'Car with indicator signal at third exit.',
    questionIds: ['q-rr-17'],
  },
  {
    id: 'rr-anim-10',
    topic: 'Roundabout Rules',
    sceneDescription: 'Car navigating multi-lane roundabout exit properly',
    klingPrompt: 'Top-down view of Dutch multi-lane roundabout. Yellow car in appropriate lane choosing correct exit. Lane discipline and exit positioning clear.',
    negativePrompt: 'traffic lights, stop signs, first-person, UK roundabout, text overlay, night, rain, cartoon, CGI, anime',
    videoUrl: null,
    durationSeconds: 4,
    viewType: 'top-down',
    freezeDescription: 'Car in correct lane at multi-lane roundabout exit.',
    questionIds: ['q-rr-20'],
  },
];
